import { DOC_LISTS, GATES, PHOTOS_SEED, PROFILE_FIELDS, SERVICE_DEFS } from './fixtures';
import type { DocState, ServiceTask, SvcCode, SvcKpi } from './types';

/** Bỏ dấu + hạ chữ thường. */
export const norm = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

export const defOf = (code: SvcCode) => SERVICE_DEFS.find((d) => d.code === code)!;

/** KPI: Finish → Đúng hạn / Trễ; Chờ bên ngoài → Dừng (đồng hồ dừng); còn lại theo giờ làm việc còn (≤ 2h = Sắp hạn). */
export function kpiOf(t: Pick<ServiceTask, 'status' | 'left'>): SvcKpi {
  if (t.status === 'Finish') return t.left < 0 ? 'Trễ' : 'Đúng hạn';
  if (t.status === 'Chờ bên ngoài') return 'Dừng';
  return t.left < 0 ? 'Trễ' : t.left <= 2 ? 'Sắp hạn' : 'Đang chạy';
}

/** Nhãn "còn / trễ" tính theo giờ LÀM VIỆC (giờ thực lấy từ work-calendar ở nơi tạo `left`). */
export function leftLabel(t: Pick<ServiceTask, 'status' | 'left'>): string {
  if (t.status === 'Finish') return t.left < 0 ? 'xong trễ' : 'đã xong';
  if (t.status === 'Chờ bên ngoài') return 'đồng hồ dừng';
  return t.left < 0 ? `trễ ${-t.left}h làm việc` : `còn ${t.left}h làm việc`;
}

/** Nhãn hạn gọn của hàng (board `__cAs`): 6h · −2h; nhãn đủ nằm ở title. */
export const leftShort = (r: Pick<ServiceTask, 'status' | 'left'>) => (r.status === 'Finish' || r.status === 'Chờ bên ngoài' ? leftLabel(r) : r.left < 0 ? `−${-r.left}h` : `${r.left}h`);
/** Bước hiện tại hiển thị: "n/tổng" và tên bước (kẹp ở bước cuối khi đã xong). */
export function stepOf(t: Pick<ServiceTask, 'code' | 'step' | 'urgent'>): { no: string; name: string } {
  const steps = defOf(t.code).steps;
  const i = Math.min(t.step, steps.length - 1);
  return { no: `${Math.min(t.step + 1, steps.length)}/${steps.length}`, name: steps[i]! + (t.urgent ? ' · Gấp' : '') };
}

/** Tìm nhanh theo ServiceID, Job, booking, khách, số hồ sơ, tàu; nhiều từ cách nhau dấu cách / , ; = OR. */
export function searchTasks(rows: readonly ServiceTask[], text: string): ServiceTask[] {
  const words = norm(text).split(/[\s,;]+/).filter(Boolean);
  if (!words.length) return [...rows];
  return rows.filter((r) => {
    const hay = norm([r.id, r.job, `HNSGN${r.job.slice(2)}`, r.booking, r.customer, r.ref, r.vessel].join(' '));
    return words.some((w) => hay.includes(w));
  });
}

export interface TaskScope {
  me?: string;
  /** 'mine' = chỉ việc của `me`, 'team' = cả team */
  scope: 'mine' | 'team';
  tab: 'all' | SvcCode;
  text?: string;
}

export function selectTasks(rows: readonly ServiceTask[], s: TaskScope): ServiceTask[] {
  const base = searchTasks(rows.filter((r) => (s.scope === 'mine' ? r.who === s.me : true)), s.text ?? '');
  return s.tab === 'all' ? base : base.filter((r) => r.code === s.tab);
}

/** Số việc CHƯA xong ở mỗi tab ("Tất cả 19", "CCF 5"…). */
export const openCount = (rows: readonly ServiceTask[], tab: 'all' | SvcCode): number =>
  rows.filter((r) => (tab === 'all' || r.code === tab) && r.status !== 'Finish').length;

export interface TaskTotals {
  open: number;
  late: number;
  done: number;
  /** % việc xong đúng hạn, '—' nếu chưa có việc xong */
  onTime: string;
}

export function totals(rows: readonly ServiceTask[]): TaskTotals {
  const done = rows.filter((r) => r.status === 'Finish');
  return {
    open: rows.filter((r) => r.status !== 'Finish').length,
    late: rows.filter((r) => kpiOf(r) === 'Trễ').length,
    done: done.length,
    onTime: done.length ? `${Math.round((done.filter((r) => r.left >= 0).length / done.length) * 100)}%` : '—',
  };
}

// ---------- Cổng rời bước ----------

export interface TaskState {
  /** giá trị hồ sơ đã nhập (đè lên dữ liệu mẫu) */
  values?: Record<string, string>;
  /** hồ sơ giấy tờ đã đánh dấu nhận: chỉ số trong DOC_LISTS */
  docsReceived?: readonly number[];
  /** ảnh đã có (đè lên dữ liệu mẫu) */
  photos?: readonly string[];
}

export interface GateIssue {
  text: string;
  tab: 'steps' | 'ai' | 'files';
  action: string;
}

/** Danh sách hồ sơ giấy tờ của việc, tính cả phần đã đánh dấu nhận. */
export const docsOf = (t: Pick<ServiceTask, 'code'>, received: readonly number[] = []): [string, DocState][] =>
  DOC_LISTS[t.code].map(([k, s], i) => [k, received.includes(i) ? 1 : s]);

/** Điều kiện còn thiếu để rời bước hiện tại. Việc đã Finish không còn điều kiện. */
export function gateIssues(t: ServiceTask, s: TaskState = {}): GateIssue[] {
  if (t.status === 'Finish') return [];
  const gates = GATES[t.code]?.[t.step] ?? [];
  const label = (key: string) => PROFILE_FIELDS[t.code].find((f) => f.key === key)?.label ?? key;
  const photos = s.photos ?? PHOTOS_SEED[t.id] ?? [];
  const out: GateIssue[] = [];
  for (const g of gates) {
    if (g === 'docs') {
      const miss = docsOf(t, s.docsReceived).filter((x) => x[1] === 0);
      if (miss.length) out.push({ text: `Hồ sơ còn thiếu: ${miss.map((x) => x[0]).join(', ')}`, tab: 'steps', action: 'Đánh dấu đã nhận' });
    } else if (g === 'lane') {
      if (!t.lane) out.push({ text: 'Chưa ghi kết quả phân luồng', tab: 'steps', action: 'Ghi luồng' });
    } else if (g === 'photoIf') {
      if ((t.lane === 'Đỏ' || t.lane === 'Soi chiếu') && !photos.includes('Kết quả kiểm hoá')) out.push({ text: `Luồng ${t.lane} · chưa có ảnh kết quả kiểm hoá`, tab: 'files', action: 'Thêm ảnh' });
    } else if (g.startsWith('f:')) {
      const key = g.slice(2);
      if (!(s.values?.[key] ?? '')) out.push({ text: `Chưa nhập ${label(key)}`, tab: 'ai', action: 'Nhập hồ sơ' });
    } else if (g.startsWith('p:')) {
      const name = g.slice(2);
      if (!photos.includes(name)) out.push({ text: `Chưa có ảnh ${name.toLowerCase()}`, tab: 'files', action: 'Thêm ảnh' });
    }
  }
  return out;
}

/** Sang bước kế: bị chặn nếu còn điều kiện; bước cuối → Finish. */
export function advance(t: ServiceTask, s: TaskState = {}): { ok: boolean; task: ServiceTask; blocked: GateIssue[] } {
  const blocked = gateIssues(t, s);
  if (blocked.length) return { ok: false, task: t, blocked };
  const n = defOf(t.code).steps.length;
  const step = Math.min(t.step + 1, n);
  return { ok: true, blocked: [], task: { ...t, step, status: step >= n ? 'Finish' : t.status === 'New' ? 'Processing' : t.status } };
}
